import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { FloatingMenu, type FloatingMenuPlacement } from './FloatingMenu'
import { Pressable } from './Pressable'
import { getNlsString } from '../../nls/nlsRuntime'
import './labeledDropdown.css'

export type SelectOption<T extends string | number> = {
  value: T
  label: ReactNode
  /** Searchable label override; defaults to stringified `label`. */
  searchText?: string
  disabled?: boolean
}

type SelectProps<T extends string | number> = {
  value: T | null
  options: SelectOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
  placeholder?: string
  emptyLabel?: string
  /** Enables the search input. Auto-enabled when options.length >= 8. */
  searchable?: boolean
  searchPlaceholder?: string
  /** Optional callback fired when search query changes. */
  onSearch?: (query: string) => void
  disabled?: boolean
  className?: string
  style?: CSSProperties
  /** Optional explicit menu placement. Defaults to `bottom-start`. */
  placement?: FloatingMenuPlacement
  /** Optional additional class for the portal menu surface. */
  menuClassName?: string
  /** Show a chevron caret on the right side of the trigger. */
  showChevron?: boolean
  /** Render-prop for a custom trigger; falls back to the default pill button. */
  renderTrigger?: (state: { open: boolean; label: ReactNode; toggle: () => void; disabled: boolean }) => ReactNode
  /** Trigger style: 'labeledPill' (LABEL | value ⌄) or 'pill' (breadcrumb style › label). Defaults to 'labeledPill'. */
  trigger?: 'pill' | 'labeledPill'
  /** Small uppercase label rendered on the left of the labeledPill trigger. Falls back to ariaLabel. */
  label?: ReactNode
  /** Outer box size: 'sm' (30px) | 'md' (36px). Defaults to 'md'. */
  size?: 'sm' | 'md'
  id?: string
}

function optionSearchText<T extends string | number>(option: SelectOption<T>): string {
  if (option.searchText) return option.searchText.toLowerCase()
  if (typeof option.label === 'string') return option.label.toLowerCase()
  if (typeof option.label === 'number') return String(option.label).toLowerCase()
  return String(option.value).toLowerCase()
}

/**
 * Drop-in replacement for the native `<select>` element. Uses FloatingMenu under
 * the hood so the popover portals to body and auto-flips. Visual surface mirrors
 * `.headerDropdown` (workspace / profile dropdowns) for consistency.
 */
export function Select<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder = getNlsString('NLSCommonInputSelectEllipsisPlaceholder'),
  emptyLabel = getNlsString('NLSCommonEmptyNoOptions'),
  searchable,
  searchPlaceholder = getNlsString('NLSCommonInputSearchEllipsisPlaceholder'),
  onSearch,
  disabled = false,
  className,
  style,
  placement = 'bottom-start',
  menuClassName,
  showChevron = true,
  renderTrigger,
  trigger = 'labeledPill',
  label,
  size = 'md',
  id,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open) {
      setQuery('')
    }
  }
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const wrapperRef = useRef<HTMLSpanElement | null>(null)

  const current = options.find((option) => option.value === value)
  const currentLabel = current?.label ?? placeholder
  const useSearch = searchable ?? options.length >= 8

  const filteredOptions = useMemo(() => {
    if (!useSearch) return options
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) => optionSearchText(option).includes(needle))
  }, [options, useSearch, query])

  const toggle = () => {
    if (disabled) return
    setOpen((prev) => !prev)
  }

  const close = () => setOpen(false)

  const labeledPillClassName = [
    'labeledDropdownTrigger',
    size === 'sm' ? 'labeledDropdownTrigger--sm' : '',
    open ? 'isOpen' : '',
    disabled ? 'isDisabled' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span
      ref={wrapperRef}
      className={`unifiedSelect ${className ?? ''}`.trim()}
      style={style}
      data-floating-menu-root="true"
    >
      {renderTrigger ? (
        <span ref={(node) => {
          // wrapped in a span; we still need a focusable element for anchor measurement.
          if (node) {
            const button = node.querySelector('button')
            anchorRef.current = button as HTMLButtonElement | null
          }
        }}>
          {renderTrigger({ open, label: currentLabel, toggle, disabled })}
        </span>
      ) : trigger === 'pill' ? (
        <span
          className={`breadcrumb breadcrumbPill breadcrumbCrumb selectPill--${size}`}
          data-breadcrumb-menu-root="true"
        >
          <Pressable
            ref={anchorRef}
            className={`breadcrumbChevron${open ? ' isOpen' : ''}`}
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-disabled={disabled ? true : undefined}
            disabled={disabled}
            onClick={toggle}
          >
            <span className="breadcrumbChevronIcon" aria-hidden="true">›</span>
          </Pressable>
          <span className="breadcrumbLabelStack">
            {options.map((option) => (
              <span
                key={`sizer-${String(option.value)}`}
                className="breadcrumbLabel"
                data-sizer="true"
                aria-hidden="true"
              >
                {option.label}
              </span>
            ))}
            <Pressable
              className="breadcrumbLabel"
              onClick={toggle}
              disabled={disabled}
              aria-disabled={disabled ? true : undefined}
            >
              {currentLabel}
            </Pressable>
          </span>
        </span>
      ) : (
        <Pressable
          ref={anchorRef}
          id={id}
          className={labeledPillClassName}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          onClick={toggle}
        >
          <span className="labeledDropdownLabel">{label ?? ariaLabel}</span>
          <span className="labeledDropdownDivider" aria-hidden="true" />
          <span className="labeledDropdownValue">{currentLabel}</span>
          {showChevron && (
            <span className="labeledDropdownCaret" aria-hidden="true">
              <ExpandMoreRoundedIcon fontSize="inherit" />
            </span>
          )}
        </Pressable>
      )}
      <FloatingMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={close}
        placement={placement}
        className={`dsDrop is-open ${menuClassName ?? ''}`.trim()}
        role="listbox"
        ariaLabel={ariaLabel}
        matchAnchorWidth
      >
        {useSearch && (
          <div className="dsDrop__search">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                onSearch?.(event.target.value)
              }}
              autoFocus
              aria-label={`${ariaLabel} search`}
            />
          </div>
        )}
        <div className="dsDrop__items">
          {filteredOptions.length === 0 ? (
            <span className="dsDrop__empty">{emptyLabel}</span>
          ) : (
            filteredOptions.map((option) => (
              <Pressable
                key={String(option.value)}
                role="option"
                aria-selected={option.value === value}
                disabled={option.disabled}
                className={`dsDrop__item${option.value === value ? ' is-active' : ''}`}
                onClick={() => {
                  if (option.disabled) return
                  onChange(option.value)
                  setOpen(false)
                }}
              >
                <span className="dsDrop__itemLabel">{option.label}</span>
              </Pressable>
            ))
          )}
        </div>
      </FloatingMenu>
    </span>
  )
}
